import { faRefresh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IGenericObject } from 'components/general/types';
import {
  ICombinationalLogic,
  IFiniteStateMachine,
  IFixedSchedule,
  ILogicalConfiguration,
  IRelativeSchedule,
  IStateTransition,
  TRoutine,
} from 'components/general/types/cdh';
import Widget from 'components/general/widgets/Widget';
import { ItemTypes } from 'config';
import ELK, { ElkNode } from 'elkjs';
import { usePermissionCheck } from 'hooks';
import {
  Dispatch,
  SetStateAction,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDrop } from 'react-dnd';
import ReactFlow, {
  ConnectionLineType,
  ConnectionMode,
  ControlButton,
  Controls,
  Edge,
  EdgeMarker,
  MarkerType,
  Node,
  OnConnect,
  OnConnectStart,
  OnConnectStartParams,
  ReactFlowProvider,
  addEdge,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useUpdateNodeInternals,
} from 'react-flow-renderer';
import theme from 'theme';
import { TRoutineTypes, WorkspaceVables } from 'utils/vable';
import { IRoutineMenuConfig, additionalDefaults, defaultRoutine } from '../RoutineForms';
import BaseEdge, { LogicalConfigurationEdge, StateTransitionEdge } from './CustomEdges/Edges';
import CombinationalLogicNode from './CustomNodes/CombinationalLogicNode';
import FiniteStateMachineNode from './CustomNodes/FiniteStateMachineNode';
import MachineElementNode from './CustomNodes/MachineElementNode';
import RoutineNode from './CustomNodes/RoutineNode';
import ScheduleNode from './CustomNodes/ScheduleNode';
import EditMenu, { ViewOnlyMenu } from './EditMenu';
import useStyles from './styles';
import {
  TRoutineBlock,
  TState,
  addBlockToState,
  deleteFromState,
  disconnectFsmSubroutine,
  disconnectLogicalConfiguration,
  disconnectSchedule,
  disconnectStateTransition,
  setDirtyBlock,
} from './utils';

interface IProps {
  state: TState;
  setState: Dispatch<SetStateAction<TState>>;
  setResetDiagram: Dispatch<SetStateAction<((state: TState) => void) | undefined>>;
}

const nodeTypes = {
  Routine: RoutineNode,
  CombinationalLogic: CombinationalLogicNode,
  FixedSchedule: ScheduleNode,
  RelativeSchedule: ScheduleNode,
  FiniteStateMachine: FiniteStateMachineNode,
  MachineElement: MachineElementNode,
};

const edgeTypes = {
  default: BaseEdge,
  LogicalConfiguration: LogicalConfigurationEdge,
  StateTransition: StateTransitionEdge,
};

// Arrow marker options for state transition edges
const edgeArrow: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  width: 40,
  height: 40,
  color: theme.palette.secondary.light,
};

// Options for the ELK layout algorithm
const elkOptions = {
  'elk.algorithm': 'mrtree', // 'layered',
  'elk.direction': 'DOWN',
  'elk.layered.spacing.nodeNodeBetweenLayers': '200',
  'elk.spacing.nodeNode': '200',
};

// Layout the nodes and edges using the ELK library mrtree algorithm
const getLayoutedElements = async (nodes: Node[], edges: Edge[], options: IGenericObject = {}) => {
  const elk = new ELK();
  const isHorizontal = options?.['elk.direction'] === 'RIGHT';
  const graph: ElkNode = {
    id: 'root',
    layoutOptions: options,
    children: nodes.map((node) => {
      const dims = { width: 100, height: 50 };
      return {
        ...node,
        // Adjust the target and source handle positions based on the layout
        // direction.
        targetPosition: isHorizontal ? 'left' : 'top',
        sourcePosition: isHorizontal ? 'right' : 'bottom',

        // Hardcode a width and height for elk to use when layouting.
        width: dims.width,
        height: dims.height,
      };
    }),
    edges: edges as any, // eslint-disable-line @typescript-eslint/no-explicit-any
  };

  return (
    elk
      .layout(graph)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((layoutedGraph: any) => ({
        nodes: layoutedGraph.children?.map((node: IGenericObject) => ({
          ...node,
          // React Flow expects a position property on the node instead of `x`
          // and `y` fields.
          position: { x: node.x, y: node.y },
        })),

        edges: layoutedGraph.edges,
      }))
      .catch(console.error)
  );
};

// Used to create unique rel ids for new blocks
const useTempId = () => {
  const id = useRef(0);
  return useCallback(() => `$temp-${id.current++}`, []);
};

interface IRoutineContext {
  state: TState;
  canEdit: boolean;
  setState: Dispatch<SetStateAction<TState>>;
  getTempId: () => string;
  deleteBlock: (id: string) => string[];
  updateType: (id: string, type: TRoutineTypes) => Edge[];
  menuConfig: IRoutineMenuConfig;
  setMenuConfig: Dispatch<SetStateAction<IRoutineMenuConfig>>;
}

const defaultContext: IRoutineContext = {
  state: { deletedBlocks: [], blocks: {}, dirtyBlocks: [] },
  canEdit: false,
  setState: () => {
    // Intentionally left blank
  },
  getTempId: () => '',
  updateType: () => [],
  deleteBlock: () => {
    return [];
  },
  menuConfig: { open: false, action: 'create', ids: [] },
  setMenuConfig: () => {
    // Intentionally left blank
  },
};

export const RoutineContext = createContext<IRoutineContext>(defaultContext);

// Custom hook to use and update a specific block
export const useRoutineState = function <T>(id: string) {
  const { state, setState } = useContext(RoutineContext);
  const data = useMemo(() => {
    return state.blocks[id] as T;
  }, [state.blocks, id]);
  const setValue = useCallback(
    (value: unknown, key?: string) => {
      setState((prev) => {
        const newState = {
          blocks: { ...prev.blocks },
          dirtyBlocks: [...prev.dirtyBlocks],
          deletedBlocks: prev.deletedBlocks,
        };
        if (key) {
          const path = key.split('.');
          const newBlock = { ...newState.blocks[id] };
          let current: IGenericObject = newBlock;
          path.forEach((key, i) => {
            if (!(key in current)) {
              current[key] = Number.isNaN(key) ? {} : [];
            }
            if (i === path.length - 1) {
              current[key] = value;
            } else {
              current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
              current = current[key];
            }
          });
          addBlockToState(newBlock, newState);
        } else addBlockToState(value as TRoutineBlock, newState);
        return newState;
      });
    },
    [setState, id]
  );
  return { data, setValue };
};

export const useRoutineMenuConfig = () => {
  const { menuConfig, setMenuConfig } = useContext(RoutineContext);
  return useMemo(
    () =>
      [menuConfig, setMenuConfig] as [
        IRoutineMenuConfig,
        Dispatch<SetStateAction<IRoutineMenuConfig>>
      ],
    [menuConfig, setMenuConfig]
  );
};

const RoutineState = (props: IProps) => {
  const { state, setState } = props;
  const canEdit = usePermissionCheck(WorkspaceVables.Permission.EDIT_BRANCH);
  const [menuConfig, setMenuConfig] = useState<IRoutineMenuConfig>({
    open: false,
    action: 'create',
    ids: [],
  });
  const getTempId = useTempId();

  const deleteBlock = useCallback(
    (id: string) => {
      const blocksToDelete: string[] = [];
      setState((prev) => {
        const newState: TState = {
          blocks: { ...prev.blocks },
          deletedBlocks: [],
          dirtyBlocks: [...prev.dirtyBlocks],
        };

        const block = newState.blocks[id] as TRoutineBlock;
        if (!block) return newState;
        if (block.type === 'LogicalConfiguration') {
          disconnectLogicalConfiguration(newState, id);
        } else if (block.type === 'StateTransition') {
          disconnectStateTransition(newState, id);
        } else {
          const routine = block as TRoutine;
          routine.parentRoutines.forEach((parent) => {
            const parentRoutine = newState.blocks[parent] as TRoutine;
            if (parentRoutine.type === 'CombinationalLogic') {
              const config = (parentRoutine as ICombinationalLogic).logicalConfigurations.find(
                (configId) => (newState.blocks[configId] as ILogicalConfiguration).routine === id
              );
              if (config) disconnectLogicalConfiguration(newState, config, { parent });
            } else if (parentRoutine.type === 'FiniteStateMachine') {
              disconnectFsmSubroutine(newState, parent, id);
            } else {
              disconnectSchedule(newState, parent, id);
            }
          });
          if (routine.type === 'CombinationalLogic') {
            const combLogic = routine as ICombinationalLogic;
            combLogic.logicalConfigurations.forEach((configId) => {
              disconnectLogicalConfiguration(newState, configId, { parent: id });
            });
          } else if (routine.type === 'FiniteStateMachine') {
            const fsm = routine as IFiniteStateMachine;
            fsm.subroutines.forEach((subroutine) => {
              disconnectFsmSubroutine(newState, id, subroutine);
            });
          } else if (routine.type === 'FixedSchedule' || routine.type === 'RelativeSchedule') {
            const schedule = routine as IFixedSchedule | IRelativeSchedule;
            schedule.subroutines.forEach((subroutine) => {
              disconnectSchedule(newState, id, subroutine);
            });
          }
          deleteFromState(id, newState);
        }
        blocksToDelete.push(...newState.deletedBlocks);
        newState.deletedBlocks = newState.deletedBlocks.concat(prev.deletedBlocks);
        return newState;
      });
      return blocksToDelete;
    },
    [setState]
  );

  // Change the type of a routine, updating the state and returning any new edges to add to the diagram
  const updateType = useCallback(
    (id: string, type: TRoutineTypes) => {
      const newEdges: Edge[] = [];
      setState((prev) => {
        const newState = {
          blocks: { ...prev.blocks },
          dirtyBlocks: [...prev.dirtyBlocks],
          deletedBlocks: [...prev.deletedBlocks],
        };
        const block = newState.blocks[id] as TRoutine;
        if (block) {
          // Delete any old, type-specific edges
          if (block.type === 'CombinationalLogic') {
            const routine = block as ICombinationalLogic;
            routine.logicalConfigurations.forEach((configId) => {
              disconnectLogicalConfiguration(newState, configId, { parent: id, configOnly: true });
            });
          } else if (block.type === 'FiniteStateMachine') {
            const routine = block as IFiniteStateMachine;
            routine.transitions.forEach((transition) =>
              disconnectStateTransition(newState, transition)
            );
          }
          // Create a new routine with the new type and any additional defaults
          const newRoutine = { ...defaultRoutine(), ...additionalDefaults(type) } as TRoutine;
          newRoutine.id = id;
          newRoutine.subroutines = block.subroutines;
          newRoutine.parentRoutines = block.parentRoutines;
          newRoutine.name = block.name;

          // Remove subroutines for leaf routine
          if (type === 'Routine') {
            newRoutine.subroutines.forEach((subroutine) => {
              newState.blocks[subroutine] = {
                ...newState.blocks[subroutine],
                parentRoutines: (newState.blocks[subroutine] as TRoutine).parentRoutines.filter(
                  (id) => id !== block.id
                ),
              };
              setDirtyBlock(subroutine, newState);
            });
            newRoutine.subroutines = [];
          }
          // Create new logical configurations for combinational logic
          else if (type === 'CombinationalLogic') {
            newRoutine.subroutines.forEach((subroutine, i) => {
              const newConfig: ILogicalConfiguration = {
                id: getTempId(),
                type: 'LogicalConfiguration',
                routine: subroutine,
                combinationalLogicRoutines: [id],
                conditions: [],
                priority: i,
              };
              newEdges.push({
                id: newConfig.id,
                source: id,
                target: subroutine,
                type: newConfig.type,
              });
              (newRoutine as ICombinationalLogic).logicalConfigurations.push(newConfig.id);
              addBlockToState(newConfig, newState);
            });
          }
          // Add default edges to show subroutine connections
          else if (
            type === 'FiniteStateMachine' ||
            type === 'FixedSchedule' ||
            type === 'RelativeSchedule'
          ) {
            newRoutine.subroutines.forEach((subroutine) => {
              newEdges.push({
                id: `${id}-${subroutine}`,
                source: id,
                target: subroutine,
                type: 'default',
              });
              // Add schedule data for schedule routines
              if (type !== 'FiniteStateMachine')
                (newRoutine as IRelativeSchedule).scheduleData[subroutine] = { activeTimes: [] };
            });
          }
          addBlockToState(newRoutine, newState);
        }
        return newState;
      });
      return newEdges;
    },
    [setState, getTempId]
  );

  const values = useMemo(
    () => ({
      state,
      setState,
      canEdit,
      getTempId,
      deleteBlock,
      updateType,
      menuConfig,
      setMenuConfig,
    }),
    [state, setState, canEdit, getTempId, updateType, deleteBlock, menuConfig, setMenuConfig]
  );

  return (
    <RoutineContext.Provider value={values}>
      <RoutinesDiagram {...props} />
    </RoutineContext.Provider>
  );
};

// Convert the routines state into react-flow nodes and edges
const initFlowState: (state: TState) => [Node[], Edge[]] = (state) => {
  const _nodes: Node[] = [];
  const _edges: Edge[] = [];

  Object.values(state.blocks).forEach((block) => {
    if (block.type === 'LogicalConfiguration') {
      const edge = block as ILogicalConfiguration;
      edge.combinationalLogicRoutines.forEach((parent) =>
        _edges.push({
          id: edge.id,
          source: parent,
          target: edge.routine,
          type: edge.type,
        })
      );
    } else if (block.type === 'StateTransition') {
      const edge = block as IStateTransition;
      _edges.push({
        id: edge.id,
        source: edge.fromState,
        target: edge.toState,
        type: edge.type,
        markerEnd: edgeArrow,
        hidden: true,
      });
    } else {
      const routine = block as TRoutine;
      _nodes.push({
        id: routine.id,
        data: routine,
        position: { x: 0, y: 0 },
        type: routine.type,
      });
      if (
        routine.type === 'FiniteStateMachine' ||
        routine.type === 'FixedSchedule' ||
        routine.type === 'RelativeSchedule'
      ) {
        routine.subroutines.forEach((subroutine) =>
          _edges.push({
            id: `${routine.id}-${subroutine}`,
            source: routine.id,
            target: subroutine,
            type: 'default',
          })
        );
      }
    }
  });
  return [_nodes, _edges];
};

const RoutinesDiagram = (props: IProps) => {
  const { state, setState, setResetDiagram } = props;
  const { getTempId, canEdit } = useContext(RoutineContext);
  const [, setMenuConfig] = useRoutineMenuConfig();
  const { classes } = useStyles();
  const { fitView, project } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>();
  const connectingNodeId = useRef<OnConnectStartParams | null>(null);
  const [fsmEdit, setFsmEdit] = useState<string | null>(null);
  const [inFullscreen, setInFullscreen] = useState(false);
  const updateInternals = useUpdateNodeInternals();

  // Fit the view when the window is fullscreened
  useEffect(() => {
    const onFullscreenChange = () => {
      fitView();
      setInFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [fitView]);

  // Calculate the initial nodes and edges when the component is mounted
  const [_nodes, _edges] = useMemo(() => initFlowState(state), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Recalculate the node layout and fit the view
  const refreshLayout = useCallback(
    (nodes, edges) => {
      getLayoutedElements(nodes, edges, elkOptions).then((layoutedElements) => {
        setNodes(layoutedElements?.nodes);
        setEdges(layoutedElements?.edges);
        fitView();
      });
    },
    [setNodes, setEdges, fitView]
  );

  // Set the resetDiagram callback to reinitialize the flow state
  // Called from the WizardSegment when the reset button is clicked
  useEffect(
    () => setResetDiagram(() => (state: TState) => refreshLayout(...initFlowState(state))),
    [refreshLayout, setResetDiagram]
  );

  // Set the initial flow state
  useEffect(() => {
    refreshLayout(_nodes, _edges);
  }, [_nodes, _edges, refreshLayout]);

  // Drop handler for adding new routines to the diagram
  const [, drop] = useDrop({
    accept: ItemTypes.ROUTINE,
    drop: (item: { type: TRoutineTypes }, monitor) => {
      addRoutine({ ...item, x: monitor.getClientOffset()?.x, y: monitor.getClientOffset()?.y });
    },
  });

  // Mark an entity as selected and open the edit menu
  // If the entity is a finite state machine, switch to fsm edit mode
  const selectEntity = useCallback(
    (id: string | null) => {
      if (id && state.blocks[id].type === 'FiniteStateMachine') {
        const fsm = state.blocks[id] as IFiniteStateMachine;
        setNodes((nds) => {
          const newNodes = nds.map((node) => ({
            ...node,
            hidden: !(node.id === id || fsm.subroutines.includes(node.id)),
            type: fsm.subroutines.includes(node.id) ? 'MachineElement' : node.type,
            data: {
              ...node.data,
              isSelected: id ? node.id === id : false,
            },
          }));
          return newNodes;
        });
        fsm.subroutines.forEach((subroutine) => updateInternals(subroutine));
        setEdges((eds) =>
          eds.map((edge) => ({ ...edge, hidden: edge.type !== 'StateTransition' }))
        );
        setFsmEdit(id);
      } else {
        setNodes((nds) =>
          nds.map((node) => ({
            ...node,
            hidden: false,
            type: state.blocks[node.id].type,
            data: { ...node.data, isSelected: id ? node.id === id : false },
          }))
        );
        setEdges((eds) =>
          eds.map((edge) => ({ ...edge, hidden: edge.type === 'StateTransition' }))
        );
        setFsmEdit(null);
      }
      setMenuConfig({ open: !!id, action: 'edit', ids: id ? [id] : [] });
    },
    [setNodes, setEdges, setMenuConfig, setFsmEdit, updateInternals, state.blocks]
  );

  // Create a new routine and add it to the state
  // Optionally set the type, position, and parent routine
  const addRoutine = useCallback(
    (props: { type?: TRoutineTypes; x?: number; y?: number; parent?: string }) => {
      const { type, x, y, parent } = props;
      const id = getTempId();
      const ids = [id];
      let edgeBlock: ILogicalConfiguration | undefined = undefined;
      let newRoutine = { ...defaultRoutine(), name: 'New Routine', id } as TRoutine;
      let position = { x: 0, y: 0 };
      if (x && y) {
        const bounds = reactFlowWrapper.current?.getBoundingClientRect();
        if (bounds) position = project({ x: x - bounds.left, y: y - bounds.top });
        position.x -= 50;
      }
      if (type) newRoutine = { ...newRoutine, type, ...additionalDefaults(type) };
      if (parent) {
        newRoutine.parentRoutines = [parent];
        const parentRoutine = state.blocks[parent] as TRoutine;
        if (parentRoutine.type === 'CombinationalLogic') {
          edgeBlock = {
            id: getTempId(),
            type: 'LogicalConfiguration',
            routine: id,
            combinationalLogicRoutines: [parent],
            conditions: [],
            priority: parentRoutine.subroutines.length,
          } as ILogicalConfiguration;
        }
      }
      setState((prev) => {
        const newState = {
          ...prev,
          blocks: { ...prev.blocks },
          dirtyBlocks: [...prev.dirtyBlocks],
        };
        if (parent) {
          const parentRoutine = { ...newState.blocks[parent] } as TRoutine;
          parentRoutine.subroutines = [...parentRoutine.subroutines, id];
          if (parentRoutine.type === 'CombinationalLogic' && edgeBlock) {
            const combLogic = parentRoutine as ICombinationalLogic;
            combLogic.logicalConfigurations = [...combLogic.logicalConfigurations, edgeBlock.id];
          }
          if (parentRoutine.type === 'RelativeSchedule' || parentRoutine.type === 'FixedSchedule') {
            const scheduler = parentRoutine as IRelativeSchedule | IFixedSchedule;
            scheduler.scheduleData = { ...scheduler.scheduleData, [id]: { activeTimes: [] } };
          }
          addBlockToState(parentRoutine, newState);
        }
        addBlockToState(newRoutine, newState);
        if (edgeBlock) {
          addBlockToState(edgeBlock, newState);
        }
        return newState;
      });
      const newNode = {
        id,
        position,
        data: { ...newRoutine, isSelected: true },
        type: newRoutine.type,
      };
      setNodes((nds) =>
        nds.map((n) => ({ ...n, data: { ...n.data, isSelected: false } })).concat(newNode)
      );
      if (parent) {
        const edgeId = edgeBlock?.id || `${parent}-${id}`;
        setEdges((eds) =>
          eds.concat({ id: edgeId, type: edgeBlock?.type || 'default', source: parent, target: id })
        );
        edgeBlock && ids.unshift(edgeId);
      }
      setMenuConfig({
        open: true,
        action: 'create',
        ids,
      });
    },
    [setMenuConfig, setState, project, setEdges, setNodes, getTempId, state.blocks]
  );

  // When the user starts drawing an edge, store the source node for the edge
  const onConnectStart: OnConnectStart = useCallback(
    (_, params) => (connectingNodeId.current = params.handleType === 'source' ? params : null),
    [connectingNodeId]
  );

  // When the user ends drawing an edge, create a new routine if the target is the background pane
  const onConnectEnd = useCallback(
    (event) => {
      if (!connectingNodeId.current?.nodeId || fsmEdit) return;
      const targetIsPane = event.target?.classList.contains('react-flow__pane');
      if (targetIsPane) {
        const parent =
          connectingNodeId.current.handleType === 'source'
            ? connectingNodeId.current.nodeId
            : undefined;
        addRoutine({ x: event.clientX, y: event.clientY, parent });
      }
    },
    [addRoutine, fsmEdit]
  );

  // Used to connect two nodes together by drawing an edge between them
  const onConnect: OnConnect = useCallback(
    (params) => {
      connectingNodeId.current = null;
      // Don't allow a routine to connect to itself unless it is a state transition
      if (params.source && params.target && (fsmEdit || params.source !== params.target)) {
        const edge: Edge = {
          id: `${params.source!}-${params.target!}`,
          source: params.source!,
          target: params.target!,
        };
        setState((prev) => {
          const newState = {
            blocks: { ...prev.blocks },
            dirtyBlocks: [...prev.dirtyBlocks],
            deletedBlocks: prev.deletedBlocks,
          };
          if (fsmEdit) {
            const newTransition = {
              id: getTempId(),
              type: 'StateTransition',
              fromState: params.source!,
              toState: params.target!,
              conditions: [],
              priority: 0,
            } as IStateTransition;
            edge.id = newTransition.id;
            edge.type = newTransition.type;
            edge.markerEnd = edgeArrow;
            const fsmBlock = { ...newState.blocks[fsmEdit] } as IFiniteStateMachine;
            fsmBlock.transitions = [...fsmBlock.transitions, newTransition.id];
            addBlockToState(newTransition, newState);
            addBlockToState(fsmBlock, newState);
          } else {
            const parent = { ...newState.blocks[params.source!] } as TRoutine;
            const child = { ...newState.blocks[params.target!] } as TRoutine;
            parent.subroutines = [...parent.subroutines, child.id];
            child.parentRoutines = [...child.parentRoutines, parent.id];
            if (parent.type === 'CombinationalLogic') {
              const combLogic = parent as ICombinationalLogic;
              const newConfig = {
                id: getTempId(),
                type: 'LogicalConfiguration',
                routine: params.target,
                combinationalLogicRoutines: [params.source],
                conditions: [],
                priority: combLogic.logicalConfigurations.length,
              } as ILogicalConfiguration;
              combLogic.logicalConfigurations = [...combLogic.logicalConfigurations, newConfig.id];
              addBlockToState(newConfig, newState);
              edge.id = newConfig.id;
              edge.type = newConfig.type;
            } else if (parent.type === 'RelativeSchedule' || parent.type === 'FixedSchedule') {
              const scheduler = parent as IRelativeSchedule | IFixedSchedule;
              scheduler.scheduleData = {
                ...scheduler.scheduleData,
                [child.id]: { activeTimes: [] },
              };
            }
            addBlockToState(parent, newState);
            addBlockToState(child, newState);
          }
          return newState;
        });
        setEdges((edges) => addEdge(edge, edges));
      }
    },
    [setState, setEdges, getTempId, fsmEdit]
  );

  return (
    // TODO: Fix hardcoded fullscreen height offset
    <div
      style={{
        position: 'relative',
        height: inFullscreen ? 'calc(100vh - 150px)' : 700, // Height offset of 150 = 20 + 64 + 42 + 24 comes from: 64 = Title, 20 = Divider, 42 = Form Buttons, 24 = form Margin
      }}
    >
      <div
        ref={(el) => {
          if (el) {
            reactFlowWrapper.current = el;
            drop(el);
          }
        }}
        style={{ height: '100%' }}
        id={`routines-diagram-container`}
      >
        <ReactFlow
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          connectionMode={fsmEdit ? ConnectionMode.Loose : ConnectionMode.Strict}
          connectionLineType={fsmEdit ? ConnectionLineType.Straight : ConnectionLineType.Bezier}
          selectNodesOnDrag={false}
          onNodeDoubleClick={(e, node) => {
            selectEntity(node.id);
          }}
          nodesConnectable={canEdit}
          elementsSelectable={canEdit}
          fitView
          minZoom={0.1}
          deleteKeyCode={null}
          className={classes.root}
        >
          <div className="edgeLabelRenderer" />
          <Controls style={{ bottom: 0, left: 0 }} showInteractive={canEdit}>
            <ControlButton title={'refresh layout'} onClick={() => refreshLayout(nodes, edges)}>
              <FontAwesomeIcon icon={faRefresh} />
            </ControlButton>
          </Controls>
        </ReactFlow>
      </div>
      {canEdit ? (
        <EditMenu selectEntity={selectEntity} />
      ) : (
        <ViewOnlyMenu selectEntity={selectEntity} fsmEdit={fsmEdit} />
      )}
    </div>
  );
};

// Wrap the diagram in a provider to allow the use of the react-flow hooks
const RoutinesDiagramWrapped = (props: IProps) => {
  return (
    <ReactFlowProvider>
      <RoutineState {...props} />
    </ReactFlowProvider>
  );
};

// Wrap the diagram in a widget for standalone use
export const RoutinesWidget = (props: IProps) => (
  <Widget title="Routines State" collapsibleConfig>
    <RoutinesDiagramWrapped {...props} />
  </Widget>
);

export default memo(RoutinesDiagramWrapped);
